import { viewerText } from "./viewer-text";
import { MapLegend } from "./MapLegend";
import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import mapWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
// MapLibre 6 needs a bundled worker; a guessed relative URL silently loses roads.
maplibregl.setWorkerUrl(mapWorkerUrl);
import type {
  GeoJSONSource,
  Map as GLMap,
  Marker,
  StyleSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import {
  warnings,
  type MapDocument,
  type MapFeature,
  type PublishedMap,
  type Coordinate,
  type Locale,
} from "@51time/domain";
import { Crosshair, Plus, Minus, Maximize2 } from "lucide-react";
type Props = {
  document: MapDocument | PublishedMap;
  mapKey: string;
  /** Original synthetic illustration used by the public demo, never a provider cache. */
  styleOverride?: StyleSpecification;
  hideLocation?: boolean;
  initialBounds?: [Coordinate, Coordinate];
  selected?: string;
  onSelect?: (id: string) => void;
  onPoint?: (point: Coordinate) => void;
  onMove?: (id: string, point: Coordinate) => void;
  onGeometryChange?: (id: string, geometry: MapFeature["geometry"]) => void;
  onView?: (center: Coordinate, zoom: number) => void;
  drawing?: Coordinate[];
  editable?: boolean;
  locale?: Locale;
  resetKey?: string;
  photoUrl?: (id: string) => string;
  editableIds?: string[];
  onStroke?: (points: Coordinate[]) => void;
  focus?: { center: Coordinate; zoom: number; nonce: number };
};
const icons: Record<string, string> = {
  point: "●",
  structure: "",
  gate: "↥",
  stop: "✕",
  arrow: "➜",
  label: "",
};
function style(base: string, key: string): string | StyleSpecification {
  return key
    ? `https://api.maptiler.com/maps/${base === "aerial" ? "hybrid" : base === "terrain" ? "topo-v2" : "streets-v2"}/style.json?key=${key}`
    : {
        version: 8,
        sources: {},
        layers: [
          {
            id: "background",
            type: "background",
            paint: { "background-color": "#e9ece3" },
          },
        ],
      };
}
function labelAnchor(geometry: MapFeature["geometry"]): Coordinate {
  if (geometry.type === "Point") return geometry.coordinates;
  const points =
    geometry.type === "LineString"
      ? geometry.coordinates
      : geometry.coordinates[0];
  return points[Math.floor((points.length - 1) / 2)];
}
export function MapCanvas(props: Props) {
  const text = viewerText[props.locale ?? "en"];
  const box = useRef<HTMLDivElement>(null),
    map = useRef<GLMap | null>(null),
    markers = useRef<Marker[]>([]),
    current = useRef(props),
    locationMarker = useRef<Marker | null>(null),
    watch = useRef<number | undefined>(undefined);
  const [mapError, setMapError] = useState(""),
    [locating, setLocating] = useState(false);
  current.current = props;
  const previousStyle = useRef({ base: props.document.basemap, key: props.mapKey, override: props.styleOverride });
  const resetView = () => {
    const m = map.current, p = current.current;
    if (p.initialBounds && m && m.getContainer().clientWidth < 480) m.jumpTo({center: p.document.center, zoom: 13.1});
    else if (p.initialBounds) m?.fitBounds(p.initialBounds, {padding: {top: 90, bottom: 120, left: 80, right: 80}, duration: 0});
    else m?.jumpTo({center: p.document.center, zoom: p.document.zoom});
  };
  const render = () => {
    const m = map.current,
      p = current.current;
    if (!m || !m.getStyle()?.layers?.length) return;
    const shapes = p.document.features.filter(
      (f) => f.geometry.type !== "Point",
    );
    const features = shapes.map((f) => ({
      type: "Feature" as const,
      id: f.id,
      geometry: f.geometry,
      properties: {
        id: f.id,
        color: f.color,
        width: f.width,
        opacity: f.opacity,
        selected: p.selected === f.id,
        private: "visibility" in f && f.visibility === "private",
      },
    }));
    if (p.drawing?.length && p.drawing.length > 1)
      features.push({
        type: "Feature",
        id: "drawing",
        geometry: { type: "LineString", coordinates: p.drawing },
        properties: {
          id: "drawing",
          color: "#f7a622",
          width: 4,
          opacity: 1,
          selected: false,
          private: false,
        },
      });
    const collection = { type: "FeatureCollection" as const, features };
    const source = m.getSource("marks") as GeoJSONSource | undefined;
    if (source) source.setData(collection);
    else {
      m.addSource("marks", { type: "geojson", data: collection });
      m.addLayer({
        id: "mark-fill",
        type: "fill",
        source: "marks",
        filter: ["==", ["geometry-type"], "Polygon"],
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.18 },
      });
      m.addLayer({
        id: "mark-halo",
        type: "line",
        source: "marks",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#fff",
          "line-width": ["+", ["get", "width"], 3],
          "line-opacity": 0.8,
        },
      });
      m.addLayer({
        id: "mark-line",
        type: "line",
        source: "marks",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": [
            "case",
            ["get", "selected"],
            "#183d34",
            ["get", "color"],
          ],
          "line-width": ["get", "width"],
          "line-opacity": ["get", "opacity"],
        },
      });
    }
    if (!m.getSource("freehand-preview")) {
      m.addSource("freehand-preview", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      m.addLayer({
        id: "freehand-preview-line",
        type: "line",
        source: "freehand-preview",
        layout: { "line-cap": "round", "line-join": "round" },
        paint: { "line-color": "#ed9d24", "line-width": 4 },
      });
    }
    markers.current.forEach((marker) => marker.remove());
    markers.current = [];
    for (const f of p.document.features) {
      if (f.geometry.type !== "Point" && !f.label && f.warning === "none")
        continue;
      const anchor = labelAnchor(f.geometry);
      const el = document.createElement("button");
      el.type = "button";
      el.dataset.warning = f.warning;
      el.className = `map-mark ${f.kind} ${p.selected === f.id ? "selected" : ""} ${"visibility" in f && f.visibility === "private" ? "private" : ""}`;
      el.setAttribute(
        "aria-label",
        f.translations[p.locale ?? "en"] || f.label ||
          (f.warning !== "none" ? warnings[f.warning][p.locale ?? "en"] : f.kind),
      );
      const symbol = document.createElement("span");
      symbol.className = "mark-symbol";
      symbol.textContent =
        f.geometry.type === "Point" ? (icons[f.kind] ?? "●") : "";
      symbol.style.color = f.color;
      if (f.kind === "arrow")
        symbol.style.transform = `rotate(${f.rotation}deg)`;
      el.append(symbol);
      if (f.label) {
        const label = document.createElement("span");
        label.className = "mark-label";
        label.textContent = f.translations[p.locale ?? "en"] || f.label;
        el.append(label);
      }
      if (f.warning !== "none") {
        const label = document.createElement("span");
        label.className = "mark-warning";
        label.textContent = Object.values(warnings[f.warning]).join("\n");
        el.append(label);
      }
      el.onclick = (e) => {
        e.stopPropagation();
        p.onSelect?.(f.id);
        if (
          !p.onSelect &&
          (f.publicNote || f.label || f.attachmentIds.length)
        ) {
          const content = document.createElement("div");
          const title = document.createElement("strong");
          title.textContent = f.translations[p.locale ?? "en"] || f.label;
          content.append(title);
          const note = document.createElement("p");
          note.textContent = f.publicNote;
          content.append(note);
          for (const id of f.attachmentIds) {
            const img = document.createElement("img");
            img.src = p.photoUrl?.(id) ?? `/api/photos/${id}`;
            img.alt =
              f.translations[p.locale ?? "en"] ||
              f.label ||
              viewerText[p.locale ?? "en"].photo;
            img.style.width = "100%";
            content.append(img);
          }
          new maplibregl.Popup({ offset: 20 })
            .setLngLat(anchor)
            .setDOMContent(content)
            .addTo(m);
        }
      };
      const marker = new maplibregl.Marker({
        element: el,
        draggable:
          !!p.editable &&
          f.geometry.type === "Point" &&
          p.selected === f.id &&
          (!p.editableIds || p.editableIds.includes(f.id)),
        anchor: "center",
      })
        .setLngLat(anchor)
        .addTo(m);
      marker.on("dragend", () => {
        const ll = marker.getLngLat();
        current.current.onMove?.(f.id, [ll.lng, ll.lat]);
      });
      markers.current.push(marker);
    }
    const selected = p.document.features.find((f) => f.id === p.selected);
    if (
      p.editable &&
      p.onGeometryChange &&
      selected &&
      selected.geometry.type !== "Point" &&
      (!p.editableIds || p.editableIds.includes(selected.id))
    ) {
      const rings =
        selected.geometry.type === "Polygon"
          ? selected.geometry.coordinates
          : [selected.geometry.coordinates];
      // Long hand drawings stay editable from their points without placing
      // thousands of HTML handles over the map at once.
      const count = rings.reduce((sum, ring) => sum + ring.length, 0);
      const step = Math.max(1, Math.ceil(count / 160));
      rings.forEach((ring, ringIndex) =>
        ring.forEach((point, index) => {
          const polygon = selected.geometry.type === "Polygon";
          if (
            (polygon && index === ring.length - 1) ||
            (index % step && index !== ring.length - 1)
          )
            return;
          const el = document.createElement("button");
          el.type = "button";
          el.className = "map-vertex";
          el.setAttribute("aria-label", `Move drawing point ${index + 1}`);
          el.onclick = (e) => e.stopPropagation();
          const marker = new maplibregl.Marker({
            element: el,
            draggable: true,
            anchor: "center",
          })
            .setLngLat(point)
            .addTo(m);
          marker.on("dragend", () => {
            if (!current.current.editable) return;
            const geometry = structuredClone(selected.geometry);
            if (geometry.type === "Point") return;
            const ll = marker.getLngLat(),
              coordinate: Coordinate = [ll.lng, ll.lat];
            if (geometry.type === "Polygon") {
              geometry.coordinates[ringIndex][index] = coordinate;
              if (index === 0)
                geometry.coordinates[ringIndex][ring.length - 1] = [
                  ...coordinate,
                ];
            } else geometry.coordinates[index] = coordinate;
            current.current.onGeometryChange?.(selected.id, geometry);
          });
          markers.current.push(marker);
        }),
      );
    }
  };
  useEffect(() => {
    if (!box.current) return;
    const p = current.current;
    const m = new maplibregl.Map({
      container: box.current,
      style: p.styleOverride ?? style(p.document.basemap, p.mapKey),
      center: p.document.center,
      zoom: p.document.zoom,
      maxZoom: 21,
      attributionControl: { compact: true },
      pitchWithRotate: false,
      dragRotate: false,
    });
    map.current = m;
    resetView();
    m.on("load", render);
    m.on("styledata", () => {
      if (!m.getSource("marks")) render();
    });
    m.on("style.load", () => {
      setMapError("");
      render();
    });
    m.on("error", () =>
      setMapError(viewerText[current.current.locale ?? "en"].mapError),
    );
    let stroke: Coordinate[] | null = null;
    const canvas = m.getCanvas();
    const coordinate = (event: PointerEvent): Coordinate => {
      const rect = canvas.getBoundingClientRect();
      const point = m.unproject([
        event.clientX - rect.left,
        event.clientY - rect.top,
      ]);
      return [point.lng, point.lat];
    };
    const pointerDown = (event: PointerEvent) => {
      if (!current.current.onStroke || event.button !== 0) return;
      event.preventDefault();
      stroke = [coordinate(event)];
      canvas.setPointerCapture(event.pointerId);
      m.dragPan.disable();
    };
    const pointerMove = (event: PointerEvent) => {
      if (!stroke) return;
      if (stroke.length < 4000) stroke.push(coordinate(event));
      const source = m.getSource("freehand-preview") as
        GeoJSONSource | undefined;
      if (source && stroke.length > 1)
        source.setData({
          type: "Feature",
          properties: {},
          geometry: { type: "LineString", coordinates: stroke },
        });
    };
    const finishStroke = () => {
      if (!stroke) return;
      const points = stroke;
      stroke = null;
      (m.getSource("freehand-preview") as GeoJSONSource | undefined)?.setData({
        type: "FeatureCollection",
        features: [],
      });
      if (points.length > 1) current.current.onStroke?.(points);
      m.dragPan.enable();
    };
    canvas.addEventListener("pointerdown", pointerDown);
    canvas.addEventListener("pointermove", pointerMove);
    canvas.addEventListener("pointerup", finishStroke);
    canvas.addEventListener("pointercancel", finishStroke);
    m.on("click", (e) => {
      const p = current.current;
      if (p.onStroke) return;
      if (p.onPoint) p.onPoint([e.lngLat.lng, e.lngLat.lat]);
      else if (p.onSelect && m.getLayer("mark-line")) {
        const hit = m.queryRenderedFeatures(e.point, {
          layers: ["mark-line", "mark-fill"],
        })[0];
        if (hit?.properties?.id) p.onSelect(hit.properties.id);
      }
    });
    m.on("moveend", () => {
      const c = m.getCenter();
      current.current.onView?.([c.lng, c.lat], m.getZoom());
    });
    return () => {
      if (watch.current !== undefined)
        navigator.geolocation.clearWatch(watch.current);
      canvas.removeEventListener("pointerdown", pointerDown);
      canvas.removeEventListener("pointermove", pointerMove);
      canvas.removeEventListener("pointerup", finishStroke);
      canvas.removeEventListener("pointercancel", finishStroke);
      m.remove();
      map.current = null;
    };
  }, []);
  useEffect(() => {
    render();
  }, [
    props.document.features,
    props.drawing,
    props.selected,
    props.editable,
    props.locale,
  ]);
  useEffect(() => {
    const previous = previousStyle.current;
    if (previous.base === props.document.basemap && previous.key === props.mapKey && previous.override === props.styleOverride) return;
    previousStyle.current = {base: props.document.basemap, key: props.mapKey, override: props.styleOverride};
    map.current?.setStyle(props.styleOverride ?? style(props.document.basemap, props.mapKey));
  }, [props.document.basemap, props.mapKey, props.styleOverride]);
  useEffect(() => {
    resetView();
  }, [props.resetKey]);
  useEffect(() => {
    if (props.focus)
      map.current?.flyTo({
        center: props.focus.center,
        zoom: props.focus.zoom,
      });
  }, [props.focus]);
  useEffect(() => {
    if (!map.current) return;
    if (props.onStroke) {
      map.current.dragPan.disable();
      map.current.getCanvas().style.cursor = "crosshair";
    } else {
      map.current.dragPan.enable();
      map.current.getCanvas().style.cursor = "";
    }
  }, [!!props.onStroke]);
  const locate = () => {
    if (locating) {
      if (watch.current !== undefined)
        navigator.geolocation.clearWatch(watch.current);
      watch.current = undefined;
      locationMarker.current?.remove();
      locationMarker.current = null;
      setLocating(false);
      return;
    }
    if (!navigator.geolocation) {
      setMapError(text.locationUnavailable);
      return;
    }
    setLocating(true);
    watch.current = navigator.geolocation.watchPosition(
      (position) => {
        const point: [number, number] = [
          position.coords.longitude,
          position.coords.latitude,
        ];
        if (!map.current) return;
        const first = !locationMarker.current;
        if (!locationMarker.current) {
          const el = document.createElement("div");
          el.className = "you-dot";
          el.title = text.locationApproximate;
          locationMarker.current = new maplibregl.Marker({ element: el })
            .setLngLat(point)
            .addTo(map.current);
        } else locationMarker.current.setLngLat(point);
        if (first)
          map.current.flyTo({
            center: point,
            zoom: Math.max(map.current.getZoom(), 15),
          });
      },
      () => {
        setMapError(text.locationPermission);
        setLocating(false);
        if (watch.current !== undefined)
          navigator.geolocation.clearWatch(watch.current);
        watch.current = undefined;
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 },
    );
  };
  return (
    <div className="map-shell">
      <div ref={box} className="map-canvas" />
      {mapError && (
        <div role="status" className="map-error">
          {mapError}
        </div>
      )}
      {!props.mapKey && !props.styleOverride && <div className="map-error">{text.noBasemap}</div>}
      <div className="map-controls">
        <button title={text.zoomIn} onClick={() => map.current?.zoomIn()}>
          <Plus />
        </button>
        <button title={text.zoomOut} onClick={() => map.current?.zoomOut()}>
          <Minus />
        </button>
        <button
          title={text.returnView}
          onClick={resetView}
        >
          <Maximize2 />
        </button>
        {!props.hideLocation && <button
          className={locating ? "active" : ""}
          title={locating ? text.locationOff : text.locationOn}
          aria-pressed={locating}
          onClick={locate}
        >
          <Crosshair />
        </button>}
      </div>
      {!!props.document.savedViews.length && (
        <select
          className="map-places"
          aria-label={text.places}
          defaultValue=""
          onChange={(e) => {
            const place = props.document.savedViews.find(
              (v) => v.id === e.target.value,
            );
            if (place)
              map.current?.flyTo({ center: place.center, zoom: place.zoom });
            e.target.value = "";
          }}
        >
          <option value="">{text.places}</option>
          {props.document.savedViews.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      )}
      <div className="map-footnote">
        {text.orientation}
        {locating ? ` · ${text.locationLocal}` : ""}
      </div>
      <MapLegend features={props.document.features} locale={props.locale} locating={locating} />
    </div>
  );
}
